
import React, { useState, useRef, useMemo } from 'react';
import { X, Upload, FileSpreadsheet, Check, Loader2, AlertCircle, Table, ArrowRight, Layers, MapPin, Info, Navigation, Play, AlertTriangle, UserCheck, RefreshCw, ChevronRight, Copy, Combine, Ban } from 'lucide-react';
import { supabase } from '../supabase';
import { User, Contact } from '../types';
import * as XLSX from 'xlsx';
import BulkGeocodeModal from './BulkGeocodeModal';

interface ImportContactsModalProps {
  onClose: () => void;
  currentUser: User;
  containerId: string;
  categories: string[];
}

type ImportStep = 'idle' | 'mapping' | 'analyzing' | 'conflicts' | 'processing' | 'geocoding_offer' | 'success';

interface ConflictData {
  spreadsheetRow: any;
  existingContact: Contact;
  matchReason: 'phone' | 'email' | 'name_address';
  action: 'skip' | 'overwrite' | 'merge';
}

const ImportContactsModal: React.FC<ImportContactsModalProps> = ({ onClose, currentUser, containerId, categories }) => {
  const [step, setStep] = useState<ImportStep>('idle');
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [fileData, setFileData] = useState<any[]>([]);
  const [csvRowsPreview, setCsvRowsPreview] = useState<string[][]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [importCount, setImportCount] = useState(0);
  const [contactsForGeo, setContactsForGeo] = useState<Contact[]>([]);
  const [showBulkGeocodeOverlay, setShowBulkGeocodeOverlay] = useState(false);

  // Conflitos
  const [conflicts, setConflicts] = useState<ConflictData[]>([]);
  const [cleanContacts, setCleanContacts] = useState<any[]>([]);

  const [mapping, setMapping] = useState({
    name: '',
    type: '',
    phones: '',
    emails: '',
    city: '',
    neighborhood: '',
    street: '',
    number: '',
    zipcode: '',
    observations: '',
    contact_origin: ''
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const normalizePhone = (p: string) => String(p || '').replace(/\D/g, '').replace(/^55/, '');
  const normalizeText = (t: string) => String(t || '').trim().toUpperCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    setErrorMsg(null);
    try {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = new Uint8Array(event.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
          if (jsonData.length < 2) throw new Error("Arquivo insuficiente.");
          const headers = (jsonData[0] as any[]).map(h => String(h || '').trim());
          setCsvHeaders(headers);
          setFileData(jsonData.slice(1));
          setCsvRowsPreview(jsonData.slice(1, 6).map(row => row.map(cell => String(cell || ''))));
          setStep('mapping');
          setMapping({
            name: headers.find(h => /nome|name|contato|cidad/i.test(h)) || '',
            type: headers.find(h => /tipo|categoria/i.test(h)) || '',
            phones: headers.find(h => /tel|fone|cel|whatsapp/i.test(h)) || '',
            emails: headers.find(h => /email|e-mail/i.test(h)) || '',
            city: headers.find(h => /cidade|municipio/i.test(h)) || '',
            neighborhood: headers.find(h => /bairro/i.test(h)) || '',
            street: headers.find(h => /rua|logradouro|street/i.test(h)) || '',
            number: headers.find(h => /num|nº|numero/i.test(h)) || '',
            zipcode: headers.find(h => /cep/i.test(h)) || '',
            observations: headers.find(h => /obs|nota|coment/i.test(h)) || '',
            contact_origin: headers.find(h => /origem|fonte|indicado/i.test(h)) || ''
          });
        } catch (err) { setErrorMsg("Erro ao ler arquivo."); } finally { setLoading(false); }
      };
      reader.readAsArrayBuffer(file);
    } catch (err) { setErrorMsg("Falha no carregamento."); setLoading(false); }
  };

  const analyzeData = async () => {
    if (!mapping.name) { alert("Coluna Nome é obrigatória."); return; }
    if (!containerId || containerId === 'undefined' || containerId === 'null') {
      setErrorMsg('Gabinete não identificado. Recarregue a página (F5) e tente novamente.');
      return;
    }
    setLoading(true);
    setStep('analyzing');
    try {
      const idx = {
        name: csvHeaders.indexOf(mapping.name),
        phones: csvHeaders.indexOf(mapping.phones),
        emails: csvHeaders.indexOf(mapping.emails),
        neighborhood: csvHeaders.indexOf(mapping.neighborhood),
        city: csvHeaders.indexOf(mapping.city),
        number: csvHeaders.indexOf(mapping.number)
      };

      const { data: existingContactsRaw, error: fetchError } = await supabase
        .from('contacts')
        .select('*')
        .eq('container_id', containerId);

      if (fetchError) throw new Error(`Erro ao carregar contatos existentes: ${fetchError.message}`);

      // Filtramos soft-deleted no cliente para não depender da coluna deleted_at existir no schema
      const existingContacts = existingContactsRaw?.filter(c => !c.deleted_at) ?? [];

      const foundConflicts: ConflictData[] = [];
      const nonConflicts: any[] = [];

      for (const row of fileData) {
        if (!row[idx.name]) continue;

        const rowName = normalizeText(row[idx.name]);
        const rowPhone = idx.phones !== -1 ? normalizePhone(row[idx.phones]) : '';
        const rowEmail = idx.emails !== -1 ? String(row[idx.emails]).toLowerCase().trim() : '';
        const rowNeighborhood = idx.neighborhood !== -1 ? normalizeText(row[idx.neighborhood]) : '';
        const rowNumber = idx.number !== -1 ? String(row[idx.number] || '').trim() : '';

        // Detecção de Duplicidade Refinada (Nome + Bairro + Número)
        const match = existingContacts?.find(ec => {
          if (rowPhone && ec.phones?.some(p => normalizePhone(p) === rowPhone)) return true;
          if (rowEmail && ec.emails?.some(e => e.toLowerCase().trim() === rowEmail)) return true;
          
          const nameMatch = normalizeText(ec.name) === rowName;
          const neighborhoodMatch = normalizeText(ec.neighborhood) === rowNeighborhood;
          const numberMatch = (ec.number || '').trim() === rowNumber;
          
          if (nameMatch && neighborhoodMatch && numberMatch) return true;
          return false;
        });

        const contactObj = {
          container_id: containerId,
          name: String(row[idx.name]).trim(),
          type: (csvHeaders.indexOf(mapping.type) !== -1 && row[csvHeaders.indexOf(mapping.type)]) ? String(row[csvHeaders.indexOf(mapping.type)]).trim() : (categories[0] || 'Cidadão'),
          phones: idx.phones !== -1 && row[idx.phones] ? [String(row[idx.phones]).trim()] : [],
          emails: idx.emails !== -1 && row[idx.emails] ? [String(row[idx.emails]).trim()] : [],
          city: idx.city !== -1 && row[idx.city] ? String(row[idx.city]).trim() : '',
          neighborhood: idx.neighborhood !== -1 && row[idx.neighborhood] ? String(row[idx.neighborhood]).trim() : '',
          street: csvHeaders.indexOf(mapping.street) !== -1 ? String(row[csvHeaders.indexOf(mapping.street)] || '').trim() : '',
          number: rowNumber,
          zipcode: csvHeaders.indexOf(mapping.zipcode) !== -1 ? String(row[csvHeaders.indexOf(mapping.zipcode)] || '').trim().replace(/\D/g, '') : '',
          observations: csvHeaders.indexOf(mapping.observations) !== -1 ? String(row[csvHeaders.indexOf(mapping.observations)] || '').trim() : '',
          contact_origin: csvHeaders.indexOf(mapping.contact_origin) !== -1 ? String(row[csvHeaders.indexOf(mapping.contact_origin)] || '').trim() : '',
          tags: [],
          created_at: new Date().toISOString()
        };

        if (match) {
          let reason: ConflictData['matchReason'] = 'name_address';
          if (rowPhone && match.phones?.some(p => normalizePhone(p) === rowPhone)) reason = 'phone';
          else if (rowEmail && match.emails?.some(e => e.toLowerCase().trim() === rowEmail)) reason = 'email';

          foundConflicts.push({
            spreadsheetRow: contactObj,
            existingContact: match,
            matchReason: reason,
            action: 'skip'
          });
        } else {
          nonConflicts.push(contactObj);
        }
      }

      setConflicts(foundConflicts);
      setCleanContacts(nonConflicts);
      setStep(foundConflicts.length > 0 ? 'conflicts' : 'processing');
      if (foundConflicts.length === 0) performFinalImport(nonConflicts, []);
    } catch (err: any) { setErrorMsg(err.message); setStep('mapping'); } finally { setLoading(false); }
  };

  const performFinalImport = async (regulars: any[], resolvedConflicts: ConflictData[]) => {
    setLoading(true);
    setStep('processing');
    try {
      let finalImported: Contact[] = [];
      
      if (regulars.length > 0) {
        const { data, error } = await supabase.from('contacts').insert(regulars).select();
        if (error) throw new Error(`Falha ao inserir contatos: ${error.message}`);
        if (data) finalImported = [...finalImported, ...data];
      }

      for (const c of resolvedConflicts) {
        if (c.action === 'skip') continue;

        if (c.action === 'overwrite') {
          const { data, error } = await supabase.from('contacts').update({
            ...c.spreadsheetRow,
            id: c.existingContact.id
          }).eq('id', c.existingContact.id).select();
          if (error) throw new Error(`Falha ao substituir contato: ${error.message}`);
          if (data) finalImported = [...finalImported, ...data];
        }
        else if (c.action === 'merge') {
          const mergedTags = [...new Set([...(c.existingContact.tags || []), ...(c.spreadsheetRow.tags || [])])];
          const mergedPhones = [...new Set([...(c.existingContact.phones || []), ...(c.spreadsheetRow.phones || [])])];
          const mergedEmails = [...new Set([...(c.existingContact.emails || []), ...(c.spreadsheetRow.emails || [])])];

          const { data, error } = await supabase.from('contacts').update({
            phones: mergedPhones,
            emails: mergedEmails,
            tags: mergedTags,
            contact_origin: c.spreadsheetRow.contact_origin || c.existingContact.contact_origin,
            observations: (c.existingContact.observations || '') + (c.spreadsheetRow.observations ? `\n--- Importação ---\n${c.spreadsheetRow.observations}` : '')
          }).eq('id', c.existingContact.id).select();
          if (error) throw new Error(`Falha ao mesclar contato: ${error.message}`);
          if (data) finalImported = [...finalImported, ...data];
        }
      }

      setImportCount(finalImported.length);
      const geoPending = finalImported.filter(c => c.street && c.city);
      if (geoPending.length > 0) { setContactsForGeo(geoPending); setStep('geocoding_offer'); }
      else { setStep('success'); }
    } catch (err: any) { setErrorMsg(err.message); setStep('conflicts'); } finally { setLoading(false); }
  };

  const setGlobalConflictAction = (action: ConflictData['action']) => {
    setConflicts(prev => prev.map(c => ({ ...c, action })));
  };

  const toggleConflictAction = (index: number, action: ConflictData['action']) => {
    setConflicts(prev => prev.map((c, i) => i === index ? { ...c, action } : c));
  };

  const mappingFields = [
    { key: 'name', label: 'NOME COMPLETO *' },
    { key: 'type', label: 'CATEGORIA / TIPO' },
    { key: 'phones', label: 'TELEFONE' },
    { key: 'emails', label: 'E-MAIL' },
    { key: 'city', label: 'CIDADE' },
    { key: 'neighborhood', label: 'BAIRRO' },
    { key: 'street', label: 'RUA / LOGRADOURO' },
    { key: 'number', label: 'NÚMERO DA RESIDÊNCIA' },
    { key: 'zipcode', label: 'CEP' },
    { key: 'contact_origin', label: 'ORIGEM DO CONTATO' },
    { key: 'observations', label: 'OBSERVAÇÕES' }
  ];

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-6 bg-navy-950/70 backdrop-blur-md animate-in fade-in" onClick={onClose}>
      <div className="bg-white dark:bg-navy-900 w-full max-w-6xl max-h-[95vh] rounded-[40px] shadow-2xl flex flex-col overflow-hidden border border-neutral-200 dark:border-navy-800 relative" onClick={e => e.stopPropagation()}>
        
        <div className="px-10 py-8 border-b border-slate-100 dark:border-navy-800 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-4">
            <div className="p-4 rounded-2xl bg-parliament-600 text-white shadow-lg"><FileSpreadsheet size={28} /></div>
            <div>
              <h2 className="text-2xl font-black text-slate-800 dark:text-white tracking-tight">Importação de Cidadãos</h2>
              <div className="flex items-center gap-2 mt-1">
                 <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Passo: {step.toUpperCase()}</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-3 hover:bg-neutral-100 dark:hover:bg-navy-800 rounded-full text-neutral-400 transition-colors"><X size={28} /></button>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-10 bg-slate-50/30 dark:bg-navy-950/20">
          
          {step === 'idle' && (
            <div className="h-full flex flex-col items-center justify-center py-20 space-y-8">
              <div className="w-24 h-24 bg-parliament-50 dark:bg-navy-800 text-parliament-600 rounded-[32px] flex items-center justify-center shadow-inner">
                {loading ? <Loader2 className="animate-spin" size={40} /> : <Upload size={40} />}
              </div>
              <div className="text-center">
                <h3 className="text-2xl font-black text-navy-950 dark:text-white mb-2">Selecione sua planilha</h3>
                <p className="text-sm font-medium text-slate-500">Analise duplicados antes de salvar na base oficial.</p>
              </div>
              <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept=".xlsx, .xls, .csv" className="hidden" />
              <button onClick={() => fileInputRef.current?.click()} className="bg-navy-950 dark:bg-parliament-600 text-white px-12 py-5 rounded-3xl font-black text-xs uppercase tracking-[0.2em] shadow-2xl hover:scale-105 active:scale-95 transition-all">ESCOLHER ARQUIVO</button>
              {errorMsg && <p className="text-red-500 font-bold text-xs bg-red-50 px-4 py-2 rounded-xl border border-red-100">{errorMsg}</p>}
            </div>
          )}

          {step === 'mapping' && (
             <div className="space-y-10 animate-in slide-in-from-bottom-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                   <div className="space-y-6">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2"><Layers size={14}/> Vínculo de Dados</p>
                      <div className="bg-white dark:bg-navy-900 p-8 rounded-[32px] border border-neutral-100 dark:border-navy-700 shadow-sm space-y-4">
                         {mappingFields.map(field => (
                           <div key={field.key} className="space-y-1.5">
                              <label className="text-[9px] font-black text-neutral-400 uppercase tracking-widest ml-1">
                                 {field.label}
                              </label>
                              <select value={(mapping as any)[field.key]} onChange={e => setMapping({...mapping, [field.key]: e.target.value})} className="w-full bg-slate-50 dark:bg-navy-900 border border-neutral-100 dark:border-navy-700 rounded-xl px-4 py-3 text-xs font-bold dark:text-white outline-none">
                                <option value="">Não importar / Ignorar</option>
                                {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                              </select>
                           </div>
                         ))}
                      </div>
                   </div>
                   <div className="space-y-6">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2"><Table size={14}/> Prévia da Planilha</p>
                      <div className="bg-white dark:bg-navy-800 rounded-[32px] border border-neutral-100 dark:border-navy-700 shadow-sm overflow-hidden overflow-x-auto h-[600px] custom-scrollbar">
                        <table className="w-full text-left text-[10px]">
                           <thead><tr className="bg-slate-50 dark:bg-navy-950 sticky top-0">{csvHeaders.map(h => <th key={h} className="px-4 py-3 font-black text-slate-400 uppercase whitespace-nowrap">{h}</th>)}</tr></thead>
                           <tbody className="divide-y divide-slate-50 dark:divide-navy-700">{csvRowsPreview.map((row, i) => (<tr key={i}>{row.map((cell, j) => <td key={j} className="px-4 py-3 font-medium text-slate-600 dark:text-slate-400 truncate max-w-[120px]">{cell}</td>)}</tr>))}</tbody>
                        </table>
                      </div>
                   </div>
                </div>
                <div className="flex justify-end pt-10"><button onClick={analyzeData} className="bg-navy-950 dark:bg-parliament-600 text-white px-12 py-5 rounded-3xl font-black text-xs uppercase tracking-[0.2em] shadow-2xl flex items-center gap-3 hover:scale-105 transition-all">ANALISAR DUPLICADOS <ArrowRight size={18}/></button></div>
             </div>
          )}

          {step === 'analyzing' && (
             <div className="h-full flex flex-col items-center justify-center py-20 space-y-6">
                <Loader2 className="animate-spin text-parliament-600" size={64} />
                <h3 className="text-2xl font-black text-navy-950 dark:text-white">Cruzando Dados...</h3>
                <p className="text-slate-500 font-medium">O sistema está verificando nomes, telefones e endereços na sua base atual.</p>
             </div>
          )}

          {step === 'conflicts' && (
             <div className="space-y-10 animate-in fade-in">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                   <div className="bg-emerald-50 dark:bg-emerald-900/10 p-8 rounded-[32px] border border-emerald-100 dark:border-emerald-800/30">
                      <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-1">Novos Contatos</p>
                      <h4 className="text-4xl font-black text-emerald-700 dark:text-emerald-400">{cleanContacts.length}</h4>
                      <p className="text-xs font-medium text-emerald-600/70 mt-2">Prontos para importar sem conflitos.</p>
                   </div>
                   <div className="md:col-span-2 bg-amber-50 dark:bg-amber-900/10 p-8 rounded-[32px] border border-amber-100 dark:border-amber-800/30 flex justify-between items-center">
                      <div>
                         <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest mb-1">Conflitos Encontrados</p>
                         <h4 className="text-4xl font-black text-amber-700 dark:text-amber-400">{conflicts.length}</h4>
                         <p className="text-xs font-medium text-amber-600/70 mt-2">Registros com dados idênticos identificados.</p>
                      </div>
                      <div className="flex gap-2">
                         <button onClick={() => setGlobalConflictAction('skip')} className="px-4 py-2 bg-white dark:bg-navy-900 border border-amber-200 rounded-xl text-[10px] font-black uppercase text-amber-700 hover:bg-amber-100 transition-all">Ignorar Todos</button>
                         <button onClick={() => setGlobalConflictAction('merge')} className="px-4 py-2 bg-amber-600 text-white rounded-xl text-[10px] font-black uppercase shadow-lg hover:bg-amber-700 transition-all">Mesclar Todos</button>
                      </div>
                   </div>
                </div>

                <div className="space-y-4">
                   <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2"><RefreshCw size={14}/> Resolução de Conflitos</p>
                   <div className="bg-white dark:bg-navy-800 border border-slate-200 dark:border-navy-700 rounded-[32px] overflow-hidden overflow-x-auto shadow-sm">
                      <table className="w-full text-left">
                         <thead>
                            <tr className="bg-slate-50 dark:bg-navy-950 text-slate-400 text-[10px] font-black uppercase border-b border-slate-100 dark:border-navy-700">
                               <th className="px-6 py-4">Na Planilha (Novo)</th>
                               <th className="px-6 py-4 w-10 text-center"></th>
                               <th className="px-6 py-4">No Sistema (Atual)</th>
                               <th className="px-6 py-4 text-center">Ação Desejada</th>
                            </tr>
                         </thead>
                         <tbody className="divide-y divide-slate-50 dark:divide-navy-700">
                            {conflicts.map((c, i) => (
                               <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                                  <td className="px-6 py-6">
                                     <p className="font-bold text-sm text-navy-950 dark:text-white">{c.spreadsheetRow.name}</p>
                                     <div className="flex gap-4 mt-2">
                                        {c.spreadsheetRow.phones[0] && <p className={`text-[10px] ${c.matchReason === 'phone' ? 'text-amber-600 font-black underline' : 'text-slate-400'}`}>{c.spreadsheetRow.phones[0]}</p>}
                                        {c.spreadsheetRow.street && <p className={`text-[10px] ${c.matchReason === 'name_address' ? 'text-amber-600 font-black underline' : 'text-slate-400'}`}>{c.spreadsheetRow.street}, {c.spreadsheetRow.number}</p>}
                                     </div>
                                  </td>
                                  <td className="px-2 py-6 text-slate-300"><ArrowRight size={20}/></td>
                                  <td className="px-6 py-6">
                                     <p className="font-bold text-sm text-navy-950 dark:text-white">{c.existingContact.name}</p>
                                     <div className="flex gap-4 mt-2">
                                        {c.existingContact.phones[0] && <p className={`text-[10px] ${c.matchReason === 'phone' ? 'text-amber-600 font-black underline' : 'text-slate-400'}`}>{c.existingContact.phones[0]}</p>}
                                        {c.existingContact.street && <p className={`text-[10px] ${c.matchReason === 'name_address' ? 'text-amber-600 font-black underline' : 'text-slate-400'}`}>{c.existingContact.street}, {c.existingContact.number}</p>}
                                     </div>
                                  </td>
                                  <td className="px-6 py-6">
                                     <div className="flex justify-center items-center bg-slate-100 dark:bg-navy-950 p-1.5 rounded-2xl border border-slate-200 dark:border-navy-700 w-fit mx-auto">
                                        <button onClick={() => toggleConflictAction(i, 'skip')} className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase transition-all flex items-center gap-1.5 ${c.action === 'skip' ? 'bg-white shadow-md text-navy-950' : 'text-slate-400'}`}><Ban size={12}/> Pular</button>
                                        <button onClick={() => toggleConflictAction(i, 'overwrite')} className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase transition-all flex items-center gap-1.5 ${c.action === 'overwrite' ? 'bg-white shadow-md text-navy-950' : 'text-slate-400'}`}><Copy size={12}/> Substituir</button>
                                        <button onClick={() => toggleConflictAction(i, 'merge')} className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase transition-all flex items-center gap-1.5 ${c.action === 'merge' ? 'bg-white shadow-md text-navy-950' : 'text-slate-400'}`}><Combine size={12}/> Mesclar</button>
                                     </div>
                                  </td>
                               </tr>
                            ))}
                         </tbody>
                      </table>
                   </div>
                </div>

                <div className="flex justify-end pt-8"><button onClick={() => performFinalImport(cleanContacts, conflicts)} className="bg-emerald-600 text-white px-16 py-5 rounded-3xl font-black text-xs uppercase tracking-[0.2em] shadow-2xl hover:scale-105 transition-all">CONFIRMAR E IMPORTAR TUDO</button></div>
             </div>
          )}

          {step === 'processing' && (
            <div className="h-full flex flex-col items-center justify-center py-20 space-y-6">
              <Loader2 className="animate-spin text-parliament-600" size={64} />
              <h3 className="text-2xl font-black text-navy-950 dark:text-white">Importando Dados Oficiais...</h3>
              <p className="text-slate-500 font-medium">Registrando as resoluções e novos contatos. Não feche esta janela.</p>
            </div>
          )}

          {step === 'geocoding_offer' && (
             <div className="h-full flex flex-col items-center justify-center py-20 space-y-8 animate-in zoom-in-95">
                <div className="w-24 h-24 bg-parliament-100 dark:bg-parliament-900/30 text-parliament-600 rounded-[32px] flex items-center justify-center shadow-xl"><Navigation size={48} className="animate-bounce" /></div>
                <div className="text-center max-w-md">
                   <h3 className="text-3xl font-black text-navy-950 dark:text-white mb-2 tracking-tighter">Importação Finalizada!</h3>
                   <p className="text-sm font-medium text-slate-500 mb-6">Registramos <span className="font-bold text-navy-900 dark:text-white">{importCount} contatos</span> (incluindo atualizações e mesclagens).</p>
                   <div className="p-6 bg-parliament-50 dark:bg-navy-950 border border-parliament-100 dark:border-navy-800 rounded-3xl mb-8">
                      <p className="text-xs font-bold text-parliament-700 dark:text-parliament-400 leading-relaxed">Deseja geolocalizar os contatos recém-adicionados agora?</p>
                   </div>
                </div>
                <div className="flex gap-4">
                   <button onClick={() => setStep('success')} className="px-8 py-4 text-slate-400 font-black text-[10px] uppercase hover:text-navy-950 transition-colors">Pular</button>
                   <button onClick={() => setShowBulkGeocodeOverlay(true)} className="bg-parliament-600 text-white px-10 py-5 rounded-3xl font-black text-xs uppercase tracking-widest shadow-2xl flex items-center gap-3 hover:scale-105 transition-all"><Play size={18} fill="currentColor" /> LOCALIZAR NO MAPA AGORA</button>

                </div>
             </div>
          )}

          {step === 'success' && (
            <div className="h-full flex flex-col items-center justify-center py-20 space-y-8 animate-in zoom-in-95">
              <div className="w-24 h-24 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 rounded-[32px] flex items-center justify-center shadow-inner"><Check size={48} strokeWidth={3} /></div>
              <div className="text-center">
                <h3 className="text-3xl font-black text-navy-950 dark:text-white mb-2 tracking-tighter">Processo Concluído</h3>
                <p className="text-slate-500 font-medium text-lg leading-relaxed max-w-sm mx-auto">Sua base de dados foi atualizada com sucesso.</p>
              </div>
              <button onClick={() => { onClose(); window.location.reload(); }} className="bg-navy-950 dark:bg-parliament-600 text-white px-12 py-5 rounded-3xl font-black text-xs uppercase tracking-[0.2em] shadow-xl transition-all hover:scale-105">VOLTAR PARA CRM</button>
            </div>
          )}
        </div>
      </div>

      {showBulkGeocodeOverlay && (
         <BulkGeocodeModal
            contacts={contactsForGeo}
            onClose={() => { setShowBulkGeocodeOverlay(false); setStep('success'); }}
            onComplete={() => { setShowBulkGeocodeOverlay(false); setStep('success'); }}
         />
      )}
    </div>
  );
};

export default ImportContactsModal;
